/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { DeviceType, useDevice } from '@squiz/dxp-console-support';
import { Dialog, NavigationItem, Overlay } from '@squiz/sds';
import { useDialogStore, useOverlayStore, useToastStore } from 'dxpConsoleCore-mf/State';
import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DxpRoutes } from '../../../DxpRoutes/DxpRoutes';
import { FrameBodyProps } from './FrameBody.props';

/**
 * DXP Console Frame body.
 */
export const FrameBody = (props: FrameBodyProps): JSX.Element => {
  const { applicationIA } = props;
  const location = useLocation();

  const { activeDialog, updateActiveDialog } = useDialogStore();
  const { activeOverlay, updateActiveOverlay } = useOverlayStore();
  const { activeToast } = useToastStore();

  // Device detection
  const deviceType = useDevice();
  const isMobile = deviceType === DeviceType.mobile;

  // Prepare routes
  const itemRoutes = useMemo(() => {
    const allSectionItems = applicationIA
      .filter((section) => section !== undefined)
      .map((section): Array<NavigationItem> => {
        return section.items;
      });

    // Flatten the sections as a first pass then look for child links
    const flatSections = allSectionItems.flat();
    return flattenRoutes(flatSections);
  }, [applicationIA]);

  // Remove active elements when the route changes
  useEffect(() => {
    if (activeDialog) {
      updateActiveDialog(null);
    }

    if (activeOverlay) {
      updateActiveOverlay(null);
    }

    // Only include the route as a dependency so that
    // enabling these elements does not trigger these actions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      <div className="main__container">
        <DxpRoutes items={itemRoutes} />
      </div>
      {activeDialog && <Dialog {...activeDialog} />}
      {activeToast}
      {activeOverlay && !isMobile && <Overlay {...activeOverlay} />}
    </>
  );
};

/**
 * Flatten application IA to a single list of items.
 * @param items A complex list of navigation items.
 * @returns A flat list of navigation items.
 */
export const flattenRoutes = (items: Array<NavigationItem>): Array<NavigationItem> => {
  const allItems = items.map((item) => {
    if (item.children) {
      return [item, ...flattenRoutes(item.children)].flat();
    }

    return item;
  });

  return allItems.flat();
};
